/* eslint-disable react/require-default-props */
import PropTypes from 'prop-types';
import _ from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { CAC_CORR_MODEL_PERFORMANCE_BODY } from '../CacConstants';
import useResizeObserver from '../chart/useResizeObserver';

const CorrModelPerfBlock = props => {
  const { chartView, varianceExplainedValues, corrResponse } = props;
  const svgRef = useRef();
  const wrapperRef = useRef();
  const resizeObserver = useResizeObserver(wrapperRef);
  // const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const [wrapperWidth, setWrapperWidth] = useState(0);

  const calcRowVariance = () => {
    return varianceExplainedValues && varianceExplainedValues.length >= 3
      ? !_.isNaN(+varianceExplainedValues[chartView[1] - 1])
        ? +varianceExplainedValues[chartView[1] - 1]
        : 0
      : 0;
  };

  const calcColVariance = () => {
    return varianceExplainedValues && varianceExplainedValues.length >= 3
      ? !_.isNaN(+varianceExplainedValues[chartView[0] - 1])
        ? +varianceExplainedValues[chartView[0] - 1]
        : 0
      : 0;
  };

  const totalVariance = () => calcColVariance() + calcRowVariance();

  const yStart = 24;
  const xPadding = 20;
  const barWidth = wrapperWidth - xPadding * 2;
  const barHeight = 26;
  const strong = barWidth * 0.65;
  const weak = barWidth * 0.35;

  const slider = (barWidth * totalVariance()) / 100.0;

  const ro = resizeObserver && resizeObserver.width;
  useEffect(() => {
    const width =
      resizeObserver && resizeObserver.width
        ? resizeObserver.width
        : wrapperRef && wrapperRef.current
        ? wrapperRef.current.clientWidth - 30
        : 0;

    if (wrapperWidth !== width) {
      setWrapperWidth(width);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ro, wrapperRef]);

  const BuildSVGSection = () => {
    return (
      <svg
        ref={svgRef}
        id="CorrModPerf"
        data-test="CorrModPerf"
        className="corrD3_svgBox"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <marker
            id="arrow"
            markerWidth="24"
            markerHeight="40"
            refX="0"
            refY="5"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M0,0 L0,10 L12,5 z" fill="#C4C4C4" />
          </marker>
        </defs>
        <g className="content">
          <rect
            x={xPadding}
            y={yStart}
            width={strong}
            height={barHeight}
            fill="#EA3812"
          />
          <line
            x1={xPadding + strong}
            y1={yStart + barHeight + 12}
            x2={xPadding + strong}
            y2={yStart}
            stroke="#4E9700"
            strokeWidth="1"
            markerStart="url(#arrow)"
          />
          <rect
            x={xPadding + strong}
            y={yStart}
            width={weak}
            height={barHeight}
            fill="#4E9700"
          />
          <text
            x={xPadding + (strong - 9)}
            y={yStart + (barHeight + 25)}
            fill="black"
          >
            65%
          </text>
        </g>
        <g className="slider1">
          <line
            x1={xPadding + slider}
            y1={yStart - 11}
            x2={xPadding + slider}
            y2={yStart + barHeight}
            stroke="#000"
            strokeWidth="1"
            markerStart="url(#arrow)"
          />
          <line
            x1={xPadding + slider}
            y1={yStart}
            x2={xPadding + slider}
            y2={yStart + barHeight}
            stroke="#000"
            strokeWidth="3"
          />
          <text
            id="totalVariance"
            x={xPadding + (slider - 9)}
            y={yStart - 14}
            fill="black"
          >{`${Math.round(totalVariance())}%`}</text>
        </g>
      </svg>
    );
  };

  const BuildVarianceSection = () => {
    return ((barWidth > 0 && varianceExplainedValues.length >= 3) && (
        <div>
          <div className="mp-block">
            <div className="mp-block-inner">
              <div
                className={classNames(
                  'mp-values',
                  { 'mp-values-weak': Math.round(totalVariance()) < 65 },
                  { 'mp-values-strong': Math.round(totalVariance()) >= 65 }
                )}
              >
                {`${Math.round(totalVariance())}%`}
              </div>
              <div className="mp-subtext">Total Variance</div>
            </div>
            <div className="mv-values-ctr">
              <div className="mp-vars">
                <div className="mp-varP">{`${Math.round(
                  calcColVariance()
                )}%`}</div>
                <div className="mp-text">{`Axis ${chartView[0]} Variance`}</div>
              </div>
              <div className="mp-vars">
                <div className="mp-varP">{`${Math.round(
                  calcRowVariance()
                )}%`}</div>
                <div className="mp-text">{`Axis ${chartView[1]} Variance`}</div>
              </div>
            </div>
            <div
              className={classNames(
                'mp-values',
                { 'mp-values-weak': Math.round(totalVariance()) < 65 },
                { 'mp-values-strong': Math.round(totalVariance()) >= 65 }
              )}
            >
              {Math.round(totalVariance()) < 65 ? 'WEAK' : 'STRONG'}
            </div>
          </div>
          {BuildSVGSection()}
        </div>
    ))
  };


  const buildHTMLBlock = () => {
    return corrResponse.errMsg ? (
      <div ref={wrapperRef}>
        Data does not support Correspondence maps.
        <svg
          ref={svgRef}
          id="CorrModPerf"
          data-test="CorrModPerf"
          className="corrD3_svgBox"
          xmlns="http://www.w3.org/2000/svg"
        />
      </div>
    ) : (
      BuildVarianceSection()
    );
  };

  return (
    <div
      className="model-corr-perf-block"
      data-test="CorrModelPerfBlock"
      ref={wrapperRef}
    >
      <div className="mp-title">Model Performance</div>
      <p className="mp-body">{CAC_CORR_MODEL_PERFORMANCE_BODY}</p>
      {buildHTMLBlock()}
    </div>
  );
};

CorrModelPerfBlock.propTypes = {
  corrResponse: PropTypes.shape({
    errMsg: PropTypes.string,
  }),
  chartView: PropTypes.arrayOf(PropTypes.number),
  varianceExplainedValues: PropTypes.arrayOf(PropTypes.number),
};

export default CorrModelPerfBlock;
